import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';
import styles from './Field.module.css';

// orbit Field의 전체 동작을 보존:
// - readonly + autoAssign = 회색 자동지정 표시
// - lockable + onChange = "수정" 버튼으로 잠금 해제 토글
// - selectOptions = <select> with chevron
// - textarea
// - suffix, hint, error
//
// 동적 컨텐츠가 많고 readonly/disabled 상태 조합이 복잡해서, 클래스 합성으로
// 처리. RoleChip처럼 단순 enum이 아니라 boolean 토글 다발.

export interface SelectOption {
  value: string;
  label: string;
}

interface FieldBaseProps {
  label: string;
  required?: boolean;
  help?: string;
  hint?: string;
  value?: string | null;
  onChange?: (next: string) => void;
  placeholder?: string;
  mono?: boolean;
  readonly?: boolean;
  autoAssign?: boolean; // readonly + 회색 'auto assigned' 표시
  error?: string;
  suffix?: ReactNode;
  after?: ReactNode;
  lockable?: boolean;
  // 라벨 행 오른쪽('수정' 버튼 옆)에 붙는 필드 전용 액션. 예: 네트워크 앱 ID 조회 버튼.
  labelAction?: ReactNode;
  // 요청 상세에서 이번 요청으로 바뀌는 칸 — 보라 테두리와 '이전 값' 한 줄을 붙인다.
  changedFrom?: string;
}

interface FieldTextProps extends FieldBaseProps {
  type?: 'text' | 'email' | 'url' | 'number';
  textarea?: false;
  selectOptions?: undefined;
  rows?: undefined;
}

interface FieldTextareaProps extends FieldBaseProps {
  textarea: true;
  rows?: number;
  type?: undefined;
  selectOptions?: undefined;
}

interface FieldSelectProps extends FieldBaseProps {
  selectOptions: SelectOption[];
  textarea?: false;
  type?: undefined;
  rows?: undefined;
}

type FieldProps = FieldTextProps | FieldTextareaProps | FieldSelectProps;

// 작은 inline SVG (orbit이 NavIcon으로 그리던 chev-d / lock 아이콘 대체)
const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const UnlockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export function Field(props: FieldProps) {
  const {
    label,
    required,
    help,
    hint,
    value,
    onChange,
    placeholder,
    mono,
    readonly,
    autoAssign,
    error,
    suffix,
    after,
    lockable,
    labelAction,
    changedFrom,
  } = props;

  const [unlocked, setUnlocked] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
  const editable = !!onChange && !readonly;
  const showLock = lockable && editable;
  const effectiveReadonly = readonly || (showLock && !unlocked);
  const isAuto = readonly && autoAssign;
  // 잠긴 값은 잠겨 보여야 한다. 잠금 버튼(lockable)이 없는 화면에서도 readonly 면
  // 회색으로 둔다 — 흰 입력칸은 '고칠 수 있다'고 말한다.
  const lockedLook = (showLock && !unlocked) || (!!readonly && !autoAssign);

  const toggleLock = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setUnlocked((prev) => {
      const next = !prev;
      if (next) {
        // refocus after unlock — orbit이 동일 동작
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return next;
    });
  };

  const inputClasses = (extra: string) =>
    [
      extra,
      mono ? styles.inputMono : '',
      unlocked ? styles.inputUnlocked : '',
      lockedLook ? styles.inputLocked : '',
      changedFrom !== undefined ? styles.inputChanged : '',
      isAuto ? styles.inputAuto : '',
      error ? styles.inputError : '',
    ]
      .filter(Boolean)
      .join(' ');

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onChange?.(event.target.value);
  };

  return (
    <label className={styles.label}>
      <div className={styles.labelRow}>
        <span className={[styles.labelText, mono ? styles.labelTextMono : ''].filter(Boolean).join(' ')}>
          {label}
        </span>
        {required && <span className={styles.required}>*</span>}
        {changedFrom !== undefined && <span className={styles.changedTag}>변경</span>}
        {help && <Tooltip text={help} />}
        {(labelAction || showLock) && (
          <span className={styles.labelActions}>
            {labelAction}
            {showLock && (
              <button
                type="button"
                onClick={toggleLock}
                className={[styles.lockBtn, unlocked ? styles.lockBtnActive : ''].filter(Boolean).join(' ')}
              >
                {unlocked ? <UnlockIcon /> : <LockIcon />}
                {unlocked ? '수정 중' : '수정'}
              </button>
            )}
          </span>
        )}
      </div>
      <div className={styles.inputWrap}>
        {props.textarea ? (
          <textarea
            ref={(el) => {
              inputRef.current = el;
            }}
            value={value ?? ''}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={effectiveReadonly}
            rows={props.rows ?? 4}
            className={inputClasses(styles.textarea)}
          />
        ) : props.selectOptions ? (
          <>
            <select
              ref={(el) => {
                inputRef.current = el;
              }}
              value={value ?? ''}
              onChange={handleChange}
              disabled={effectiveReadonly}
              className={inputClasses(styles.select)}
            >
              {props.selectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className={styles.chevron} aria-hidden>
              <ChevronDownIcon />
            </span>
          </>
        ) : (
          <input
            ref={(el) => {
              inputRef.current = el;
            }}
            type={props.type ?? 'text'}
            value={value ?? ''}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={effectiveReadonly}
            className={inputClasses(styles.input)}
          />
        )}
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>
      {changedFrom !== undefined && (
        <div className={styles.changedFrom}>
          이전 <s>{changedFrom}</s>
        </div>
      )}
      {hint && !error && <div className={styles.hint}>{hint}</div>}
      {error && <div className={styles.error}>{error}</div>}
      {after}
    </label>
  );
}
